// Ticket scoring — compares a saved ticket to actual race results and
// computes the $ payout / net P&L.
//
//   result = { race, wps: [W, P, S], exotics: {Exacta: {base, combo, payoff}, ...} }
//   ticket = { kind, race, base, combos, horses: [{posts:[...]}, ...] }
//
// A "hit" means the actual finishing order is fully covered by the ticket's
// horse groups:
//   DD:       W(leg1) in group[0] AND W(leg2) in group[1]
//   Exacta:   W in group[0] AND P in group[1]
//   Trifecta: W in group[0] AND P in group[1] AND S in group[2]

const inGroup = (post, group) => Array.isArray(group) && group.includes(post);
const num = (x) => (Number.isFinite(Number(x)) ? Number(x) : null);

const noResult = (t) => ({
  hit: false,
  finishers: {},
  groups: (t.horses || []).map((g) => (g?.posts || []).slice()),
  payoff: null,
  payoffBase: null,
  pool_combo: null,
  net: -Number(t.base || 0) * Number(t.combos || 0),
});

// Compute payout $ for a hit ticket:
// Cybertote publishes a $BASE payoff. User's ticket has a `base` amount per
// combo. Scaled payout = pool_payoff * (ticket_base / pool_base).
// User only wins on ONE winning combo, so payout is per single combo,
// while cost was total = base * combos.
const scaledPayout = (poolBase, poolPayoff, ticketBase) => {
  if (!poolBase || !poolPayoff) return null;
  return (poolPayoff * ticketBase) / poolBase;
};

const scoreExactaLike = (t, wps, groups, poolInfo) => {
  if (!wps || wps.length < groups.length) return noResult(t);
  const finishers = {};
  const labels = ["W", "P", "S"];
  for (let i = 0; i < groups.length; i += 1) finishers[labels[i]] = wps[i];
  let hit = true;
  for (let i = 0; i < groups.length; i += 1) {
    if (!inGroup(wps[i], groups[i])) { hit = false; break; }
  }
  const cost = Number(t.base) * Number(t.combos);
  if (!hit) {
    return {
      hit: false,
      finishers,
      groups,
      payoff: null,
      payoffBase: null,
      pool_combo: poolInfo?.combo || null,
      net: -cost,
    };
  }
  const payoff = scaledPayout(poolInfo?.base, poolInfo?.payoff, Number(t.base));
  return {
    hit: true,
    finishers,
    groups,
    payoff,
    payoffBase: poolInfo?.base ?? null,
    pool_combo: poolInfo?.combo ?? null,
    net: payoff != null ? payoff - cost : -cost,
  };
};

export const scoreTicket = (t, resultsByRace) => {
  if (!t || !resultsByRace) return null;
  const groups = (t.horses || []).map((g) => (g?.posts || []).map(Number));
  const race = num(t.race);
  if (race == null) return null;

  if (t.kind === "Exacta") {
    const r = resultsByRace[race];
    if (!r?.wps || r.wps.length < 2) return null;
    return scoreExactaLike(t, r.wps.slice(0, 2), groups.slice(0, 2), r.exotics?.Exacta);
  }
  if (t.kind === "Trifecta") {
    const r = resultsByRace[race];
    if (!r?.wps || r.wps.length < 3) return null;
    // Key Box: hit = key in top-3 AND the other two top-3 are both in `with`.
    if (t.shape === "keybox") {
      const key = Number(t.key);
      const withPosts = (t.with_posts || []).map(Number);
      const top3 = r.wps.slice(0, 3).map(Number);
      const finishers = { W: top3[0], P: top3[1], S: top3[2] };
      const keyInTop3 = top3.includes(key);
      const others = top3.filter((p) => p !== key);
      // Still need exactly 2 "others" that are both in With.
      const othersInWith =
        others.length === 2 && others.every((p) => withPosts.includes(p));
      const hit = keyInTop3 && othersInWith;
      const cost = Number(t.base) * Number(t.combos);
      const info = r.exotics?.Trifecta;
      const payoff = hit
        ? scaledPayout(info?.base, info?.payoff, Number(t.base))
        : null;
      return {
        hit,
        finishers,
        groups,
        payoff,
        payoffBase: info?.base ?? null,
        pool_combo: info?.combo ?? null,
        net: hit && payoff != null ? payoff - cost : -cost,
      };
    }
    return scoreExactaLike(t, r.wps.slice(0, 3), groups.slice(0, 3), r.exotics?.Trifecta);
  }
  if (t.kind === "DD") {
    const r1 = resultsByRace[race];
    const r2 = resultsByRace[race + 1];
    if (!r1?.wps?.length || !r2?.wps?.length) return null;
    const finishers = { "L1-W": r1.wps[0], "L2-W": r2.wps[0] };
    const hit = inGroup(r1.wps[0], groups[0]) && inGroup(r2.wps[0], groups[1]);
    const cost = Number(t.base) * Number(t.combos);
    // DD payoff comes from leg-2's exotics table (Cybertote convention).
    const info = r2.exotics?.DD;
    const payoff = hit ? scaledPayout(info?.base, info?.payoff, Number(t.base)) : null;
    return {
      hit,
      finishers,
      groups,
      payoff,
      payoffBase: info?.base ?? null,
      pool_combo: info?.combo ?? null,
      net: hit && payoff != null ? payoff - cost : -cost,
    };
  }
  return null;
};
